import { useState } from 'react';
import { useAuditLogs, useAuditExport } from '../../hooks/useAdminApi';
import { AuditLogRequest, AuditLogEntry } from '../../api/admin-api';
import { AdminPagination } from './AdminUi';

const TABLE_LABELS: Record<string, string> = {
    CarrierRecommendation:      'Classic Appetite',
    CarrierAppetiteByUkw:       'Universal Appetite',
    CarrierBusinessClass:       'Business Class (Classic)',
    CarrierBusinessClassByUkw:  'Business Class (Universal)',
};
const friendlyTable = (t: string) => TABLE_LABELS[t] ?? t;

const ACTION_LABELS: Record<string, string> = {
    INSERT: 'Created',
    UPDATE: 'Updated',
    DELETE: 'Deleted',
};

const actionBadgeClass = (action: string) =>
    action === 'INSERT' ? 'admin-badge-on'
    : action === 'DELETE' ? 'admin-badge-off'
    : 'admin-badge-warn';

function tryPretty(raw: string | null | undefined) {
    if (!raw) return '—';
    try { return JSON.stringify(JSON.parse(raw), null, 2); }
    catch { return raw; }
}

export const AuditLogs = () => {
    const [params, setParams] = useState<AuditLogRequest>({ page: 1, pageSize: 25 });
    const [actionFilter, setActionFilter] = useState('');
    const [tableFilter, setTableFilter] = useState('');
    const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

    const { data, isLoading } = useAuditLogs({
        ...params,
        action: actionFilter || undefined,
        tableName: tableFilter || undefined,
    });
    const exportMutation = useAuditExport();

    const handleExport = async () => {
        const blob = await exportMutation.mutateAsync(params);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'change-history-export.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <h1>Change History</h1>
                    <p className="admin-page-subtitle">
                        Complete audit trail of all data changes applied to the carrier database. Exportable to CSV.
                    </p>
                </div>
                <button type="button" className="admin-btn admin-btn-primary" onClick={handleExport} disabled={exportMutation.isPending}>
                    {exportMutation.isPending
                        ? <><span className="admin-spin" /> Exporting…</>
                        : <><span className="admin-icon icon-download" aria-hidden="true" /> Export CSV</>}
                </button>
            </div>

            <div className="admin-filters">
                <div className="select-box admin-select-sm">
                    <select value={actionFilter}
                        onChange={e => { setActionFilter(e.target.value); setParams(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Operations</option>
                        <option value="INSERT">Created</option>
                        <option value="UPDATE">Updated</option>
                        <option value="DELETE">Deleted</option>
                    </select>
                </div>
                <div className="select-box admin-select-wide">
                    <select value={tableFilter}
                        onChange={e => { setTableFilter(e.target.value); setParams(p => ({ ...p, page: 1 })); }}>
                    <option value="">All Record Types</option>
                    <option value="CarrierRecommendation">Classic Appetite</option>
                    <option value="CarrierAppetiteByUkw">Universal Appetite</option>
                    <option value="CarrierBusinessClass">Business Class (Classic)</option>
                    <option value="CarrierBusinessClassByUkw">Business Class (Universal)</option>
                    </select>
                </div>
            </div>

            {isLoading ? (
                <div className="admin-empty">
                    <span className="admin-spin admin-spin-lg" />
                    <p>Loading change history…</p>
                </div>
            ) : (
                <>
                    <div className="admin-table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Operation</th>
                                    <th>Record Type</th>
                                    <th>Record ID</th>
                                    <th>Changed By</th>
                                    <th>Date &amp; Time</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.data.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="admin-table-empty">
                                            No records match the current filters.
                                        </td>
                                    </tr>
                                )}
                                {data?.data.map(entry => (
                                    <tr key={entry.id}>
                                        <td className="admin-muted">{entry.id}</td>
                                        <td>
                                            <span className={`admin-badge ${actionBadgeClass(entry.action)}`}>
                                                {ACTION_LABELS[entry.action] ?? entry.action}
                                            </span>
                                        </td>
                                        <td>{friendlyTable(entry.tableName)}</td>
                                        <td className="admin-muted">#{entry.recordId}</td>
                                        <td>{entry.performedBy || '—'}</td>
                                        <td className="admin-nowrap">{new Date(entry.performedAt).toLocaleString()}</td>
                                        <td className="admin-actions">
                                            <button type="button" className="admin-action-btn"
                                                onClick={() => setSelectedEntry(entry)}>
                                                View details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <AdminPagination
                        page={params.page || 1}
                        pageSize={params.pageSize || 25}
                        totalCount={data?.totalCount || 0}
                        onPageChange={p => setParams(prev => ({ ...prev, page: p }))}
                    />
                </>
            )}

            {selectedEntry && (
                <div className="admin-modal-backdrop" onClick={() => setSelectedEntry(null)}>
                    <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
                        <h3>Change Details</h3>
                        <p className="admin-muted">
                            {ACTION_LABELS[selectedEntry.action] ?? selectedEntry.action} · {friendlyTable(selectedEntry.tableName)} · Record #{selectedEntry.recordId}
                        </p>
                        <p className="admin-muted">
                            {selectedEntry.performedBy} · {new Date(selectedEntry.performedAt).toLocaleString()}
                        </p>
                        {selectedEntry.changeRequestId != null && (
                            <p className="admin-muted">Change request: <strong>CR-{selectedEntry.changeRequestId}</strong></p>
                        )}
                        {selectedEntry.oldValues && (
                            <div className="admin-detail-block">
                                <p className="admin-meta-label">Previous Values</p>
                                <pre className="admin-pre">{tryPretty(selectedEntry.oldValues)}</pre>
                            </div>
                        )}
                        {selectedEntry.newValues && (
                            <div className="admin-detail-block">
                                <p className="admin-meta-label">New Values</p>
                                <pre className="admin-pre">{tryPretty(selectedEntry.newValues)}</pre>
                            </div>
                        )}
                        <div className="admin-modal-actions">
                            <button type="button" className="admin-btn admin-btn-primary" onClick={() => setSelectedEntry(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
